import { useCallback, useMemo, useRef, useState } from "react";

const SESSION_KEY = "agm_seva_session";
const ENDPOINT = "/admin/api/ai/public-chat";
const LEAD_ENDPOINT = "/admin/api/ai/lead";

function ensureSessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto?.randomUUID?.() || `s-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `s-${Date.now()}`;
  }
}

export function useSevaGuide(sourcePage) {
  const sessionId = useMemo(ensureSessionId, []);
  const [messages, setMessages] = useState([
    {
      role: "bot",
      text: "Namaste. I can help you understand AGM, our seva programs, Vedant, donations and how to get involved. How can I help?",
      actions: [],
    },
  ]);
  const [busy, setBusy] = useState(false);
  const sending = useRef(false);

  const send = useCallback(
    async (text) => {
      const message = (text || "").trim();
      if (!message || sending.current) return;
      sending.current = true;
      setBusy(true);
      setMessages((m) => [...m, { role: "user", text: message, actions: [] }]);
      try {
        const res = await fetch(ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId, message, sourcePage }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json().catch(() => null);
        const reply = data?.reply || "Sorry, I could not respond. Please use the contact form.";
        setMessages((m) => [
          ...m,
          {
            role: "bot",
            text: reply,
            actions: data?.suggestedActions || [],
            leadPrompt: data?.leadPrompt,
          },
        ]);
      } catch {
        setMessages((m) => [
          ...m,
          {
            role: "bot",
            text: "I had trouble responding. Please try again or contact the AGM team.",
            actions: [{ label: "Contact team", href: "/#contact" }],
          },
        ]);
      } finally {
        setBusy(false);
        sending.current = false;
      }
    },
    [sessionId, sourcePage]
  );

  const submitLead = useCallback(
    async ({ kind, fields }) => {
      try {
        const res = await fetch(LEAD_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ sessionId, kind, region: "Global", fields }),
        });
        const data = await res.json().catch(() => null);
        if (!res.ok || !data?.success) {
          return {
            ok: false,
            message:
              data?.message || "Sorry, we couldn't save your details. Please use the contact form.",
          };
        }
        setMessages((m) => [
          ...m,
          {
            role: "bot",
            text: data.message || "Thank you — the AGM team has your details and will follow up.",
            actions: [],
          },
        ]);
        return { ok: true, message: data.message };
      } catch {
        return {
          ok: false,
          message: "Sorry, we couldn't save your details. Please use the contact form.",
        };
      }
    },
    [sessionId]
  );

  return { sessionId, messages, busy, send, submitLead };
}
